import React, { useState, useRef, useCallback } from "react";
import { Platform, View, Pressable, StyleSheet } from "react-native";
import { CodeIcon, PencilSimpleIcon, Warning } from "@phosphor-icons/react";
import TextEditor from "./TextEditor";
import MobileTextEditor from "./MobileTextEditor";
import HtmlSourceEditor from "./HtmlSourceEditor";
import CustomText from "../common/general/CustomText/CustomText";
import CustomButton from "../common/general/CustomButton/CustomButton";
import CustomModal from "../common/general/CustomModal/CustomModal";
import CustomSpacing from "../common/layout/CustomSpacing/CustomSpacing";
import { useIframeAllowedDomains } from "../../hooks/useIframeAllowedDomains";

type EditorMode = "visual" | "html";

const CustomRichTextEditor = ({
  value,
  onChange,
  isSubmitting,
  placeholder,
  activelyEditing,
  makeImagesPublic = false,
  restrictImageUpload = false,
  allowHtmlMode = false,
  onModeChange,
}: {
  value: string;
  onChange: (content: string) => void;
  isSubmitting: boolean;
  placeholder: string;
  activelyEditing: boolean;
  makeImagesPublic?: boolean;
  restrictImageUpload?: boolean;
  allowHtmlMode?: boolean;
  onModeChange?: (mode: EditorMode) => void;
}) => {
  const isWeb = Platform.OS === "web";
  const [editorMode, setEditorMode] = useState<EditorMode>("visual");
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const pendingModeRef = useRef<EditorMode | null>(null);
  const tenantDomains = useIframeAllowedDomains();

  const showModeToggle = allowHtmlMode && isWeb;

  const hasContent = Boolean(value?.trim());

  const requestModeSwitch = useCallback(
    (targetMode: EditorMode) => {
      if (targetMode === editorMode) return;
      if (hasContent) {
        pendingModeRef.current = targetMode;
        setShowSwitchConfirm(true);
      } else {
        setEditorMode(targetMode);
        onModeChange?.(targetMode);
      }
    },
    [editorMode, hasContent, onModeChange]
  );

  const handleConfirmSwitch = useCallback(() => {
    const nextMode = pendingModeRef.current ?? "visual";
    onChange("");
    setEditorMode(nextMode);
    onModeChange?.(nextMode);
    pendingModeRef.current = null;
    setShowSwitchConfirm(false);
  }, [onChange, onModeChange]);

  const handleCancelSwitch = useCallback(() => {
    pendingModeRef.current = null;
    setShowSwitchConfirm(false);
  }, []);

  const renderModeToggle = () => {
    if (!showModeToggle) return null;

    const targetLabel = pendingModeRef.current === "html" ? "HTML" : "Visual";

    return (
      <>
        <View style={styles.toggleContainer}>
          <Pressable
            style={[styles.toggleButton, editorMode === "visual" && styles.toggleButtonActive]}
            onPress={() => requestModeSwitch("visual")}
            accessibilityLabel="Visual editor"
            role="button">
            <PencilSimpleIcon size={14} color={editorMode === "visual" ? "#344054" : "#667085"} />
            <CustomText
              text="Visual Editor"
              size="xs"
              weight={editorMode === "visual" ? "bold" : "medium"}
              useTranslationText={false}
              style={editorMode === "visual" ? styles.toggleTextActive : styles.toggleText}
            />
          </Pressable>
          <Pressable
            style={[styles.toggleButton, editorMode === "html" && styles.toggleButtonActive]}
            onPress={() => requestModeSwitch("html")}
            accessibilityLabel="HTML source editor"
            role="button">
            <CodeIcon size={14} color={editorMode === "html" ? "#344054" : "#667085"} />
            <CustomText
              text="HTML Editor"
              size="xs"
              weight={editorMode === "html" ? "bold" : "medium"}
              useTranslationText={false}
              style={editorMode === "html" ? styles.toggleTextActive : styles.toggleText}
            />
          </Pressable>
        </View>

        {editorMode === "html" && (
          <View style={styles.modeBanner}>
            <Warning size={16} color="#b45309" />
            <CustomText
              text="You are in HTML mode. The Visual editor is not available while HTML mode is active. Switch back to Visual to use the rich-text editor."
              size="xs"
              useTranslationText={false}
              style={styles.modeBannerText}
            />
          </View>
        )}

        <CustomModal
          visible={showSwitchConfirm}
          onRequestClose={handleCancelSwitch}
          title={`Switch to ${targetLabel} editor?`}
          showCloseButton
          translateTitle={false}>
          <CustomText
            text="The Visual and HTML editors are independent — content does not carry over between them. Switching will clear your current content."
            size="s"
            useTranslationText={false}
            style={styles.modalMessage}
          />
          <CustomSpacing type="vertical" size="m" />
          <View style={styles.modalButtons}>
            <CustomButton
              text="Cancel"
              styleType="lightTransparent"
              onPress={handleCancelSwitch}
              accessibilityLabel="Cancel and stay in current editor"
            />
            <CustomButton
              text={`Clear & switch to ${targetLabel}`}
              styleType="primary"
              onPress={handleConfirmSwitch}
              accessibilityLabel={`Clear content and switch to ${targetLabel} editor`}
            />
          </View>
        </CustomModal>
      </>
    );
  };

  const renderEditor = () => {
    if (editorMode === "html" && isWeb) {
      return (
        <HtmlSourceEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={isSubmitting}
          iframeDomainAllowlist={tenantDomains}
        />
      );
    }

    if (isWeb) {
      return (
        <TextEditor
          value={value}
          onChange={(content) => onChange(content)}
          disabled={isSubmitting}
          placeholder={placeholder}
          makeImagesPublic={makeImagesPublic}
          restrictImageUpload={restrictImageUpload}
        />
      );
    }

    return (
      <MobileTextEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        activelyEditing={activelyEditing}
        makeImagesPublic={makeImagesPublic}
        restrictImageUpload={restrictImageUpload}
      />
    );
  };

  return (
    <View>
      {renderModeToggle()}
      {renderEditor()}
    </View>
  );
};

const styles = StyleSheet.create({
  toggleContainer: {
    flexDirection: "row",
    marginBottom: 8,
    backgroundColor: "#f3f4f6",
    borderRadius: 8,
    padding: 4,
    alignSelf: "flex-start",
  },
  toggleButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  toggleButtonActive: {
    backgroundColor: "#ffffff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  toggleText: {
    color: "#667085",
  },
  toggleTextActive: {
    color: "#344054",
  },
  modeBanner: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "#fffbeb",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#fcd34d",
  },
  modeBannerText: {
    flex: 1,
    color: "#92400e",
    lineHeight: 18,
  },
  modalMessage: {
    color: "#374151",
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
});

export default CustomRichTextEditor;
